import { useState, useEffect, useCallback } from 'react'
import { ref, get, getDatabase, onValue, off } from 'firebase/database'
import { initializeApp } from 'firebase/app'
import { toastr } from 'react-redux-toastr'
import { defaultWpMenu } from 'layouts/shop/WpHeader/defaultWpMenu'

/**
 * Custom hook to fetch menu items from Firebase Realtime Database
 *
 * This hook connects to the Firebase Realtime Database at:
 * https://uniprest-magic-test.europe-west1.firebasedatabase.app/
 *
 * It fetches data from the specified path (defaults to 'menu') and provides loading and error states.
 * The hook automatically updates when the data changes in Firebase.
 *
 * Usage example:
 * ```jsx
 * // Default usage - fetches from 'menu' path
 * const { menuItems, loading, error, refreshMenuItems } = useWpMenuItems();
 *
 * // Custom path usage
 * const { menuItems: headerData, loading, error } = useWpMenuItems('header');
 *
 * if (loading) return <div>Loading...</div>;
 * if (error) return <div>Error: {error}</div>;
 *
 * return (
 *   <div>
 *     {menuItems.map(item => (
 *       <div key={item.id}>{item.text}</div>
 *     ))}
 *     <button onClick={refreshMenuItems}>Refresh</button>
 *   </div>
 * );
 * ```
 *
 * @param {string} path - The path in the Firebase database to fetch data from (default: 'menu')
 * @returns {Object} An object containing:
 *   - menuItems: Array of items from Firebase
 *   - loading: Boolean indicating if data is being fetched
 *   - error: Error message if fetch failed, null otherwise
 *   - refreshMenuItems: Function to manually refresh the data
 */
const useWpMenuItems = (path = 'headerMenu') => {
  const [menuItems, setMenuItems] = useState(defaultWpMenu)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [database, setDatabase] = useState(null)
  const [menuRef, setMenuRef] = useState(null)

  // Initialize Firebase and set up references
  useEffect(() => {
    try {
      // Initialize Firebase with the test database configuration
      const firebaseConfig = {
        // Using the test database URL as specified in the requirements
        databaseURL: 'https://uniprest-magic-test.europe-west1.firebasedatabase.app/'
      }

      // Initialize Firebase app with a unique name to avoid conflicts
      const app = initializeApp(firebaseConfig, 'wpMenuItems')
      
      // Get database reference
      const db = getDatabase(app)
      setDatabase(db)
      
      // Reference to the data in the database at the specified path
      const mRef = ref(db)
      setMenuRef(mRef)
    } catch (err) {
      console.error?.('Error initializing Firebase:', err)
      setError(err?.message)
      setLoading(false)
      toastr?.error?.('Error', 'Failed to initialize Firebase. Please try again later.')
    }
  }, [path])

  // Set up real-time listener when menuRef is available
  useEffect(() => {
    if (!menuRef) return

    setLoading(true)
    
    // Set up real-time listener
    const unsubscribe = onValue(
      menuRef,
      (snapshot) => {
        if (snapshot?.exists?.()) {
          // If data exists, set it to state
          setMenuItems(snapshot?.val?.())
        } else {
          // If no data exists, set empty array
          setMenuItems([])
          console.warn?.('No menu data found in Firebase')
        }
        setLoading(false)
      },
      (err) => {
        console.error?.('Error fetching menu items from Firebase:', err)
        setError(err?.message)
        setLoading(false)
        toastr?.error?.('Error', 'Failed to load menu items. Please try again later.')
      }
    )

    // Clean up listener on unmount
    return () => {
      if (menuRef) {
        off?.(menuRef)
      }
    }
  }, [menuRef])

  // Create a memoized fetch function that can be called on demand
  const fetchMenuItems = useCallback(async () => {
    if (!menuRef || !database) {
      toastr?.error?.('Error', 'Firebase not initialized. Please try again later.')
      return
    }

    setLoading(true)
    setError(null)
    
    try {
      // Fetch the data manually (useful for refresh button)
      const snapshot = await get(menuRef)
      
      if (snapshot?.exists?.()) {
        // If data exists, set it to state
        setMenuItems(snapshot?.val?.())
      } else {
        // If no data exists, set empty array
        setMenuItems([])
        console.warn?.('No menu data found in Firebase')
      }
      
      setLoading(false)
    } catch (err) {
      console.error?.('Error fetching menu items from Firebase:', err)
      setError(err?.message)
      setLoading(false)
      toastr?.error?.('Error', 'Failed to load menu items. Please try again later.')
    }
  }, [menuRef, database, path])

  return {
    menuItems,
    loading,
    error,
    refreshMenuItems: fetchMenuItems // Expose refresh function
  }
}

export default useWpMenuItems